import { useState } from 'react'

import { format } from 'date-fns/format'
import type { DateRange } from 'react-day-picker'
import * as XLSX from 'xlsx'

import ExportIcon from '@/assets/icons/icon-export.svg'
import { handleError } from '@/utils/error'
import { classNames } from '@/utils/helpers'

import { Button } from './button'
import { useToast } from './toast'

export type SheetExtension = 'csv' | 'xlsx'

export type ExportProps = {
  extension?: SheetExtension
  disabled?: boolean
  testId?: string
  className?: string
} & (
  | {
      getFromServer?: undefined
      getTableSheetData: () => object[]
      fileName: string
    }
  | {
      getFromServer: () => Promise<void>
      getTableSheetData?: undefined
      fileName?: undefined
    }
)

export const Export = ({
  getFromServer,
  getTableSheetData,
  fileName,
  extension = 'csv',
  disabled,
  className,
  testId = 'export-button',
}: ExportProps) => {
  const [isDataLoading, setIsDataLoading] = useState(false)
  const toast = useToast()

  const handleClickExport = async () => {
    if (getFromServer) {
      setIsDataLoading(true)
      toast.info('Starting export. It could take a few minutes.')
      await getFromServer().catch((error: unknown) => {
        handleError(
          error,
          'An unexpected error occurred while exporting data. Please try again or contact support if the issue persists.',
        )
      })
      setIsDataLoading(false)
    } else {
      const data = getTableSheetData()

      if (data.length === 0) return

      const ws = XLSX.utils.json_to_sheet(data)
      const wb = XLSX.utils.book_new()

      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      XLSX.writeFile(wb, fileName + '.' + extension)
    }
  }

  return (
    <Button
      onClick={handleClickExport}
      disabled={isDataLoading || disabled}
      isLoading={isDataLoading}
      className={classNames(
        'text-text-default hover:text-primary-500 shrink-0',
        className,
      )}
      variant="ghost"
      testId={testId}
    >
      <ExportIcon className="mr-1" />
      Export
    </Button>
  )
}

const formatDate = (date: Date) => format(date, 'LLL d yyyy')

export const getFormattedDateRange = (date: DateRange) =>
  [date.from, date.to]
    .filter((d): d is Date => Boolean(d))
    .map(formatDate)
    .join(' - ')
